
import React, { createContext, useContext } from 'react';
import { PaymentContextType } from './PaymentContextTypes';
import { useAuth } from '@/context/AuthProvider';
import { useLanguage } from '@/context/LanguageContext';
import {
  usePaymentTransactions,
  usePaymentCards,
  usePaymentUsers,
  useBluetoothDevices,
  usePaymentProcessing,
  useRecipientData,
  useStripeState
} from './hooks';

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Import all our custom hooks
  const { transactions, addTransaction } = usePaymentTransactions();
  const { debitCards, addDebitCard } = usePaymentCards();
  const { users, addUser, currentUser, setCurrentUser } = usePaymentUsers();
  const { 
    bluetoothDevices, 
    addBluetoothDevice,
    updateBluetoothDeviceConnection,
    updateBluetoothDeviceTransfer,
    removeBluetoothDevice,
    clearBluetoothDevices,
    isScanning,
    startScan,
    stopScan,
    connectedDevice,
    setConnectedDevice,
    connectToDevice,
    disconnectDevice,
    availableDevices
  } = useBluetoothDevices();

  const {
    processPayment,
    processAiPayment,
    processEnhancedAiStripePayment,
    processAiCardPayment,
    processAiStripePayment,
    aiCardVerify,
    
    // Verification status states
    aiVerificationStatus,
    setAiVerificationStatus,
    paymentStatus,
    setPaymentStatus,
    aiVerificationScore,
    setAiVerificationScore,
    aiRiskLevel,
    setAiRiskLevel,
    
    // Stripe states
    stripeReady,
    setStripeReady,
    stripeError,
    setStripeError,
    cardBrand,
    setCardBrand,
    cardLast4,
    setCardLast4,
    cardDetails,
    setCardDetails,
    
    // AI Stripe states
    aiStripeVerificationStatus,
    setAiStripeVerificationStatus,
    aiStripeVerificationScore,
    setAiStripeVerificationScore,
    aiStripeRiskLevel,
    setAiStripeRiskLevel,
    aiStripeCardBrand,
    setAiStripeCardBrand,
    aiStripeCardLast4,
    setAiStripeCardLast4,
    aiStripeError,
    setAiStripeError,
    aiStripeReady,
    setAiStripeReady,
  } = usePaymentProcessing(addTransaction);
  
  const {
    recipientUsername,
    setRecipientUsername,
    recipientPhone,
    setRecipientPhone,
    recipientEmail,
    setRecipientEmail,
    findUserByUsername,
    verifyUserAccount,
  } = useRecipientData();
  
  const {
    isStripeInitialized,
    initializeStripe,
  } = useStripeState();

  // Combine all the hook data into our context value
  const value: PaymentContextType = {
    transactions,
    addTransaction,
    debitCards,
    addDebitCard,
    users,
    addUser,
    currentUser,
    setCurrentUser,
    bluetoothDevices,
    addBluetoothDevice,
    updateBluetoothDeviceConnection,
    updateBluetoothDeviceTransfer,
    removeBluetoothDevice,
    clearBluetoothDevices,
    processPayment,
    processAiPayment,
    processEnhancedAiStripePayment,
    aiVerificationStatus,
    setAiVerificationStatus,
    paymentStatus,
    setPaymentStatus,
    aiVerificationScore,
    setAiVerificationScore,
    aiRiskLevel,
    setAiRiskLevel,
    stripeReady,
    setStripeReady,
    stripeError,
    setStripeError,
    cardBrand,
    setCardBrand,
    cardLast4,
    setCardLast4,
    setCardDetails,
    cardDetails,
    setAiStripeVerificationStatus,
    aiStripeVerificationStatus,
    setAiStripeVerificationScore,
    aiStripeVerificationScore,
    setAiStripeRiskLevel,
    aiStripeRiskLevel,
    setAiStripeCardBrand,
    aiStripeCardBrand,
    setAiStripeCardLast4,
    aiStripeCardLast4,
    setAiStripeError,
    aiStripeError,
    setAiStripeReady,
    aiStripeReady,
    
    // Additional properties from hooks
    isScanning,
    connectedDevice,
    availableDevices,
    startScan,
    stopScan,
    connectToDevice,
    disconnectDevice,
    setConnectedDevice,
    recipientUsername,
    setRecipientUsername,
    recipientPhone,
    setRecipientPhone,
    recipientEmail,
    setRecipientEmail,
    findUserByUsername,
    verifyUserAccount,
    isStripeInitialized,
    initializeStripe,
    processAiCardPayment,
    processAiStripePayment,
    aiCardVerify
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

// Also export the context for direct usage if needed
export { PaymentContext };
